import { Injectable } from '@angular/core';

import { Observable, pipe, UnaryFunction, firstValueFrom, tap } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { ReactiveStore } from '@app/shared/rsm';
import { emitOnce } from '@ngneat/elf';
import { Filter } from '@app/shared/components/filter/filter.component';

import {
  AttributesApiService,
  initState,
  AttributesState,
  AttributesViewModel,
  AttributesStore,
  OrganizationAttribute,
  GetOrganizationAttributesResponse,
  LoadOptions,
} from '@dg/attributes-data-access';

import { getEnabledFacets } from '../utils';

@Injectable({ providedIn: 'root' }) // Explicitly register this Facade as a root singleton so DI is available for global modal dialog injections.
export class AttributesFacade {
  public vm$: Observable<AttributesViewModel>;
  public attributes$: Observable<OrganizationAttribute[]>;

  private _store = new ReactiveStore<AttributesState, OrganizationAttribute>(
    'attributes',
    initState
  );

  constructor(private attributesApi: AttributesApiService) {
    // Stream the AttributesViewModel
    this.vm$ = this._store.state$.pipe(
      this.buildViewModel(),
      shareReplay<AttributesViewModel>({ refCount: true })
    );
    this.attributes$ = this._store.entities$;
  }

  /**
   * Exposed method to load attributes for a specified organization
   */
  public async init(orgId: number) {
    const { pageNum, term, take, filters } = initState();
    await this.loadAttributes({ orgId, pageNum, term, take, filters });
  }

  public save(attribute: Partial<OrganizationAttribute>, orgId: number) {
    return this.attributesApi.saveAttributeChanges(attribute, orgId).pipe(
      tap(() => {
        const _attribute = this._store.findItemByID<OrganizationAttribute>(
          attribute.attributeId.toString()
        );
        const updatedAttribute = {
          ..._attribute,
          ...attribute,
        } as OrganizationAttribute;

        this._store.upsertItems([updatedAttribute]);
      })
    );
  }

  public checkTypeMismatch(
    attribute: Partial<OrganizationAttribute>,
    orgId: number
  ) {
    return this.attributesApi.checkTypeMismatch(attribute, orgId);
  }

  // *************************************************
  // Internal View Model API methods
  // *************************************************

  private search(term: string) {
    this.loadAttributes({ term, pageNum: 1 });
  }

  private changePage(pageNum: number) {
    this.loadAttributes({ pageNum });
  }

  private changePageSize(take: number) {
    this.loadAttributes({ take });
  }

  private filter(filterEvent: { filters: Filter[]; id: string }) {
    const enabledFacets = getEnabledFacets(filterEvent.filters);

    this.loadAttributes({ filters: enabledFacets, pageNum: 1 });
  }

  // *************************************************
  // Internal View Model fabrication
  // *************************************************

  /**
   * Build an RXJS operator that will build the ViewModel when the store changes
   */
  private buildViewModel(): UnaryFunction<
    Observable<AttributesStore>,
    Observable<AttributesViewModel>
  > {
    return pipe(
      map((store: AttributesStore) => {
        const { entities, ids, ...state } = store;
        const attributes = store.ids.map((id: string) => store.entities[id]);
        return {
          ...state, // exclude entities and ids
          attributes,
          search: this.search.bind(this),
          filter: this.filter.bind(this),
          changePage: this.changePage.bind(this),
          changePageSize: this.changePageSize.bind(this),
        } as AttributesViewModel;
      })
    );
  }

  // *************************************************
  // Internal async REST methods
  // *************************************************
  private async loadAttributes(loadOptions: Partial<LoadOptions>) {
    const mergedLoadOptions = this.validateOptions(loadOptions);
    const { attributes, total } = await firstValueFrom(
      this.lookupOrgAttributes(mergedLoadOptions)
    );

    emitOnce(() => {
      this._store.deleteEntities({ deleteAll: true });
      this._store.update(
        this.handleStateUpdates(total, mergedLoadOptions),
        attributes
      );
    });
  }

  /**
   * Load organization attributes and default visibility setting
   */
  private lookupOrgAttributes(
    loadOptions: LoadOptions
  ): Observable<GetOrganizationAttributesResponse> {
    const { orgId, term, pageNum, take, filters } = loadOptions;

    return this.attributesApi.getOrganizationAttributes({
      orgId,
      take,
      pageNum,
      term,
      filters,
    });
  }

  private validateOptions(options?: Partial<LoadOptions>): LoadOptions {
    const { orgId, term, filters, pageNum, take } = this._store.snapshot;

    const mergedOptions = {
      orgId: options?.orgId ?? orgId,
      term: options?.term ?? term,
      filters: options?.filters ?? filters,
      pageNum: options?.pageNum ?? pageNum,
      take: options?.take ?? take,
    };
    return mergedOptions;
  }

  private handleStateUpdates(
    totalResults: number,
    options?: Partial<LoadOptions>
  ) {
    return (state: AttributesState) => {
      state.orgId = options?.orgId;
      state.term = options?.term;
      state.filters = options?.filters;
      state.take = options?.take;
      state.pageNum = options?.pageNum;
      state.totalResults = totalResults;
    };
  }
}
